import type {Object3D} from 'three';
import {
	type ExternalGeometrySourceDescriptor,
	resolveExternalGeometrySourceDescriptorKey,
	resolveExternalGeometrySourceOwnerGroupKey,
} from '../../model/ExternalGeometrySourceDescriptor';
import type {CreateGeometryReferencesInput} from '../../model/SketchEntityReferenceTypes';
import type {CreateSketchEntityReferencesResult} from '../../model/SketchEntityReferenceResults';
import type {SketchEntityReferenceRecord} from '../../model/SketchEntityReferenceRecord';
import {createExecutionDiagnostic, type ExecutionDiagnostic,} from '../../model/SketchEntityReferenceDiagnostics';
import {REFERENCE_TARGET_CREATED_COMMIT_REASON} from '../../model/SketchEntityReferencePropagation';
import {
	type SketchEntityReferenceClosedProfileFinalizePort,
	SketchEntityReferenceClosedProfileFinalizer,
} from './SketchEntityReferenceClosedProfileFinalizer';
import type {
	SketchEntityReferenceCreateCommitCoordinatorDependencies,
	SketchEntityReferenceSuccessfulCreateBatch as SuccessfulCreateBatch,
} from '../../../../../types/reference/sketch-entity-reference/SketchEntityReferenceCreateCommitCoordinator';
import type { RemoveGeometryReferencesResult } from '../../../../../types/reference/sketch-entity-reference/RemoveGeometryReferencesService';

export type {
	SketchEntityReferenceCreateCommitCoordinatorDependencies,
	SketchEntityReferenceSuccessfulCreateBatch,
} from '../../../../../types/reference/sketch-entity-reference/SketchEntityReferenceCreateCommitCoordinator';

/**
 * 将混合来源按宿主上下文拆为子批次。
 * 说明：
 * 1. sketch-entity 按 `sourceSketchId` 分组；
 * 2. feature-edge 按 `owningObjectId` 分组；
 * 3. topology-edge 按稳定 topology owner 分组；
 * 4. 保留 Map 插入顺序，确保回滚顺序与原始输入一致。
 *
 * @param createInput 原始创建输入。
 * @returns 可独立执行的子批次集合。
 */
function splitIntoSubBatches(
	createInput: CreateGeometryReferencesInput,
): CreateGeometryReferencesInput[] {
	const groupMap = new Map<string, ExternalGeometrySourceDescriptor[]>();

	for (const descriptor of createInput.sources) {
		const groupKey = resolveExternalGeometrySourceOwnerGroupKey(descriptor);
		const bucket = groupMap.get(groupKey) ?? [];
		bucket.push(descriptor);
		groupMap.set(groupKey, bucket);
	}

	return Array.from(groupMap.values()).map((sources) => ({
		targetSketchId: createInput.targetSketchId,
		sources,
		sourceDisplayLabelsByKey: pickSourceDisplayLabelsByKey(
			sources,
			createInput.sourceDisplayLabelsByKey,
		),
		selectionIntentsBySourceKey: pickSelectionIntentsBySourceKey(
			sources,
			createInput.selectionIntentsBySourceKey,
		),
		runtimeTopologySourcesBySourceKey: pickRuntimeTopologySourcesBySourceKey(
			sources,
			createInput.runtimeTopologySourcesBySourceKey,
		),
		options: createInput.options,
	}));
}

/**
 * 为提交子批次保留选择阶段生成的纯数据 topology 来源快照。
 *
 * 该映射只存在于当前命令调用栈中；拆批不得丢失它，否则 Create 会退回场景
 * sidecar 二次重定位并与 canonical descriptor 产生虚假冲突。
 *
 * @param sources 当前提交子批次来源。
 * @param sourcesBySourceKey 原始命令的纯数据临时来源映射。
 * @returns 只包含当前子批次来源的运行时快照映射。
 */
function pickRuntimeTopologySourcesBySourceKey(
	sources: ExternalGeometrySourceDescriptor[],
	sourcesBySourceKey: CreateGeometryReferencesInput[
		'runtimeTopologySourcesBySourceKey'
	],
): CreateGeometryReferencesInput['runtimeTopologySourcesBySourceKey'] {
	if (!sourcesBySourceKey) {
		return undefined;
	}
	const entries = sources.flatMap((source) => {
		const sourceKey = resolveExternalGeometrySourceDescriptorKey(source);
		const runtimeSource = sourcesBySourceKey[sourceKey];
		return runtimeSource ? [[sourceKey, runtimeSource] as const] : [];
	});
	return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

/**
 * 为子批次挑选来源显示标签。
 *
 * 设计说明：
 * 1. Convert Entities 的已选列表编号属于整次会话，而不是每个拆分子批次；
 * 2. 若拆批时丢掉 `sourceDisplayLabelsByKey`，不同类型运行时对象名会各自从 0 兜底，导致多个 `边线1`；
 * 3. 因此子批次必须保留属于自身 sources 的稳定标签映射，让创建服务继续使用会话编号。
 *
 * @param sources 子批次来源。
 * @param sourceDisplayLabelsByKey 原始会话来源标签映射。
 * @returns 子批次来源标签映射；没有可用标签时返回 undefined。
 */
function pickSourceDisplayLabelsByKey(
	sources: ExternalGeometrySourceDescriptor[],
	sourceDisplayLabelsByKey: Record<string, string> | undefined,
): Record<string, string> | undefined {
	if (!sourceDisplayLabelsByKey) {
		return undefined;
	}

	const entries = sources
		.map((source): [string, string] | null => {
			const sourceKey = resolveExternalGeometrySourceDescriptorKey(source);
			const sourceDisplayLabel = sourceDisplayLabelsByKey[sourceKey];
			return sourceDisplayLabel ? [sourceKey, sourceDisplayLabel] : null;
		})
		.filter((entry): entry is [string, string] => entry !== null);

	return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

/**
 * 为子批次挑选来源选择意图快照。
 *
 * 设计说明：
 * 1. 拆批只改变执行分组，不改变用户原始选择意图；
 * 2. 因此每条 source 的 intent snapshot 必须随 source key 原样进入对应子批；
 * 3. 未命中的 key 不应生成空快照，避免 create service 写入错误记录语义。
 *
 * @param sources 子批次来源。
 * @param selectionIntentsBySourceKey 原始选择意图映射。
 * @returns 子批次选择意图映射；没有可用快照时返回 undefined。
 */
function pickSelectionIntentsBySourceKey(
	sources: ExternalGeometrySourceDescriptor[],
	selectionIntentsBySourceKey: CreateGeometryReferencesInput['selectionIntentsBySourceKey'],
): CreateGeometryReferencesInput['selectionIntentsBySourceKey'] {
	if (!selectionIntentsBySourceKey) {
		return undefined;
	}

	const entries = sources
		.map((source): [string, NonNullable<CreateGeometryReferencesInput['selectionIntentsBySourceKey']>[string]] | null => {
			const sourceKey = resolveExternalGeometrySourceDescriptorKey(source);
			const selectionIntent = selectionIntentsBySourceKey[sourceKey];
			return selectionIntent ? [sourceKey, selectionIntent] : null;
		})
		.filter((entry): entry is [string, NonNullable<CreateGeometryReferencesInput['selectionIntentsBySourceKey']>[string]] => entry !== null);

	return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

/**
 * 草图实体引用创建提交协调器。
 * 说明：
 * 1. 该类只负责拆批、聚合与失败回滚；
 * 2. 真实创建动作交给注入的 service 执行；
 * 3. 这样可以在 Task 1 就先稳定流程骨架，而不提前迁移完整服务实现。
 */
export class SketchEntityReferenceCreateCommitCoordinator {
	/** 转换实体引用提交后的闭合轮廓收口器。 */
	private readonly closedProfileFinalizer: SketchEntityReferenceClosedProfileFinalizePort;


	/**
	 * @param dependencies 创建与回滚依赖。
	 */
	public constructor(
		private readonly dependencies: SketchEntityReferenceCreateCommitCoordinatorDependencies,
	) {

		this.closedProfileFinalizer = dependencies.closedProfileFinalizer
			?? new SketchEntityReferenceClosedProfileFinalizer();

	}

	/**
	 * 提交一次混合来源创建请求。
	 *
	 * @param createInput 创建输入。
	 * @returns 聚合后的创建结果。
	 */
	public async commit(
		createInput: CreateGeometryReferencesInput,
	): Promise<CreateSketchEntityReferencesResult> {
		const subBatches = splitIntoSubBatches(createInput);
		const aggregatedDiagnostics: CreateSketchEntityReferencesResult['diagnostics'] = [];
		const aggregatedReferences: SketchEntityReferenceRecord[] = [];
		const aggregatedReferenceIds: string[] = [];
		const aggregatedTargetEntityIds: string[] = [];
		const successfulBatches: SuccessfulCreateBatch[] = [];

		for (const subBatch of subBatches) {
			let batchResult: CreateSketchEntityReferencesResult;
			try {
				batchResult = await this.dependencies.executeBatch(subBatch);
			} catch (error) {
				aggregatedDiagnostics.push(createExecutionDiagnostic({
					kind: 'create-batch-failed',
					level: 'error',
					message: `执行创建子批次失败: ${error instanceof Error ? error.message : String(error)}`,
				}));
				const rollbackDiagnostics = await this.rollback(successfulBatches);
				aggregatedDiagnostics.push(...rollbackDiagnostics);
				return {
					createdReferences: [],
					createdReferenceIds: [],
					createdTargetEntityIds: [],
					diagnostics: aggregatedDiagnostics,
				};
			}
			aggregatedDiagnostics.push(...batchResult.diagnostics);

			const hasError = batchResult.diagnostics.some((diagnostic) => diagnostic.level === 'error');
			if (hasError) {
				const rollbackDiagnostics = await this.rollback([
					...successfulBatches,
					{
						targetSketchId: subBatch.targetSketchId,
						createdReferenceIds: batchResult.createdReferenceIds,
						createdTargetEntityIds: batchResult.createdTargetEntityIds,
					},
				]);
				aggregatedDiagnostics.push(...rollbackDiagnostics);
				return {
					createdReferences: [],
					createdReferenceIds: [],
					createdTargetEntityIds: [],
					diagnostics: aggregatedDiagnostics,
				};
			}

			aggregatedReferenceIds.push(...batchResult.createdReferenceIds);
			aggregatedTargetEntityIds.push(...batchResult.createdTargetEntityIds);
			aggregatedReferences.push(...batchResult.createdReferences);
			successfulBatches.push({
				targetSketchId: subBatch.targetSketchId,
				createdReferenceIds: batchResult.createdReferenceIds,
				createdTargetEntityIds: batchResult.createdTargetEntityIds,
			});
		}

		try {
			const closedProfileChangedObjectIdsBySketch = this.finalizeClosedProfiles(successfulBatches);

			return {
				createdReferences: aggregatedReferences.map(reference => structuredClone(reference)),
				createdReferenceIds: aggregatedReferenceIds,
				createdTargetEntityIds: aggregatedTargetEntityIds,
				diagnostics: [
					...aggregatedDiagnostics,
					...this.markSuccessfulTargetSketchesDirty(
						successfulBatches,
						closedProfileChangedObjectIdsBySketch,
					),
				],
			};
		} catch (error) {
			/**
			 * 闭合轮廓收口与目标草图定义提交仍属于同一次创建命令。
			 * 任何一步抛错都必须通过统一 Removal 入口反向清理已经落地的
			 * Record、关系、tag 与目标实体，禁止把半完成创建返回给调用方。
			 */
			aggregatedDiagnostics.push(createExecutionDiagnostic({
				kind: 'create-batch-failed',
				level: 'error',
				message: `转换实体引用创建收口失败: ${error instanceof Error ? error.message : String(error)}`,
				referenceId: aggregatedReferenceIds[0],
				targetEntityId: aggregatedTargetEntityIds[0],
			}));
			aggregatedDiagnostics.push(...await this.rollback(successfulBatches));

			return {
				createdReferences: [],
				createdReferenceIds: [],
				createdTargetEntityIds: [],
				diagnostics: aggregatedDiagnostics,
			};
		}
	}

	/**
	 * 回滚已经完成创建提交的引用集合。
	 *
	 * 该入口供创建后的历史提交失败补偿复用，确保仍经过统一 Removal 任务，
	 * 而不是由调用方直接删除 Repository 记录。
	 *
	 * @param referenceIds 已经落地的引用记录 ID。
	 * @returns 统一移除任务的逐条结果与诊断。
	 */
	public rollbackCreatedReferences(
		referenceIds: readonly string[],
	): Promise<RemoveGeometryReferencesResult> {
		return this.dependencies.rollbackCreatedReferences(referenceIds);
	}

	/**
	 * 在转换实体引用全部子批次成功后执行闭合轮廓收口。
	 *
	 * 设计说明：
	 * 1. Convert Entities 可能把一个闭合链拆成多个来源子批次创建；
	 * 2. 因此闭合判断必须等全部子批次成功后统一执行；
	 * 3. 这里只负责组织目标草图和目标对象，实际闭合策略交给端口实现。
	 *
	 * @param successfulBatches 已成功创建的批次。
	 * @returns 按目标草图分组的额外变更对象 ID。
	 */
	private finalizeClosedProfiles(
		successfulBatches: SuccessfulCreateBatch[],
	): Map<string, string[]> {
		const changedObjectIdsByTargetSketch = new Map<string, string[]>();
		if (!this.dependencies.getSketchGroup || successfulBatches.length <= 0) {
			return changedObjectIdsByTargetSketch;
		}

		const shouldSyncContour = this.dependencies.shouldSyncShadedSketchContours?.() ?? false;
		const batchesByTargetSketch = new Map<string, SuccessfulCreateBatch[]>();
		successfulBatches.forEach((batch) => {
			const batches = batchesByTargetSketch.get(batch.targetSketchId) ?? [];
			batches.push(batch);
			batchesByTargetSketch.set(batch.targetSketchId, batches);
		});

		batchesByTargetSketch.forEach((batches, targetSketchId) => {
			const targetSketch = this.dependencies.getSketchGroup?.(targetSketchId);
			if (!targetSketch) {
				return;
			}

			const createdTargetEntityIds = [...new Set(batches.flatMap((batch) => batch.createdTargetEntityIds))];
			const targetObjects = createdTargetEntityIds
				.map((objectId) => this.dependencies.getSketchObject(targetSketchId, objectId))
				.filter((targetObject): targetObject is Object3D => targetObject !== null);
			const finalizeResult = this.closedProfileFinalizer.finalize({
				targetSketch,
				targetObjects,
				syncContour: shouldSyncContour,
			});
			const changedObjectIds = [
				...finalizeResult.createdCollectionIds,
				...finalizeResult.syncedStandaloneContourOwnerIds,
			];
			if (changedObjectIds.length > 0) {
				changedObjectIdsByTargetSketch.set(targetSketchId, changedObjectIds);
			}
		});

		return changedObjectIdsByTargetSketch;
	}


	/**
	 * 标记成功创建引用目标所产生的目标草图定义变更。
	 *
	 * 设计说明：
	 * 1. Convert Entities 创建仍属于当前草图编辑会话，命令完成后只进入 Model Undo 历史；
	 * 2. 下游 Feature 的权威提交边界是退出草图，创建命令不能提前发布 committed；
	 * 3. 这里统一补充 dirty 元数据，确保即使几何落地未移动既有点，退出草图也能识别本次编辑。
	 *
	 * @param successfulBatches 已成功创建的批次。
	 * @returns dirty 标记阶段诊断。
	 */
	private markSuccessfulTargetSketchesDirty(
		successfulBatches: SuccessfulCreateBatch[],
		closedProfileChangedObjectIdsBySketch: Map<string, string[]> = new Map(),
	): ExecutionDiagnostic[] {
		if (!this.dependencies.getSketchGroup) {
			return [];
		}

		const diagnostics: ExecutionDiagnostic[] = [];
		const batchesByTargetSketch = new Map<string, SuccessfulCreateBatch[]>();
		successfulBatches.forEach((batch) => {
			const batches = batchesByTargetSketch.get(batch.targetSketchId) ?? [];
			batches.push(batch);
			batchesByTargetSketch.set(batch.targetSketchId, batches);
		});

		batchesByTargetSketch.forEach((batches, targetSketchId) => {
			const createdTargetEntityIds = [...new Set(batches.flatMap((batch) => batch.createdTargetEntityIds))];
			const createdReferenceIds = [...new Set(batches.flatMap((batch) => batch.createdReferenceIds))];
			const changedObjectIds = [
				...new Set([
					...createdTargetEntityIds,
					...(closedProfileChangedObjectIdsBySketch.get(targetSketchId) ?? []),
				]),
			];
			const sourceObjectId = createdTargetEntityIds[0];
			if (!sourceObjectId) {
				return;
			}

			const targetSketch = this.dependencies.getSketchGroup?.(targetSketchId);
			if (!targetSketch) {
				diagnostics.push(createExecutionDiagnostic({
					kind: 'create-batch-failed',
					level: 'warning',
					message: `转换实体引用创建后标记目标草图 dirty 失败：未找到目标草图 ${targetSketchId}`,
					targetEntityId: sourceObjectId,
				}));
				return;
			}

			const metadata = {
				reason: REFERENCE_TARGET_CREATED_COMMIT_REASON,
				sourceObjectId,
				changedObjectIds,
				originReferenceIds: createdReferenceIds,
			};
			targetSketch.markDefinitionDirty(metadata);
		});

		return diagnostics;
	}

	/**
	 * 回滚一组已落地的批次。
	 * 说明：
	 * 1. 按逆序回滚，避免后创建对象先依赖前创建对象时出现残留；
	 * 2. 参数既可能是已成功批次，也可能包含“当前失败但已部分落地”的批次；
	 * 3. 先删引用记录，再清理目标对象及其标记；
	 * 4. 正式创建链路会把部分控制点直接挂到 sketch group，因此这里必须连同关联点一起回收。
	 *
	 * @param batchesToRollback 需要回滚的批次快照。
	 */
	private async rollback(batchesToRollback: SuccessfulCreateBatch[]): Promise<ExecutionDiagnostic[]> {
		const rollbackDiagnostics: ExecutionDiagnostic[] = [];

		for (const batchToRollback of [...batchesToRollback].reverse()) {
			const { createdReferenceIds } = batchToRollback;
			if (createdReferenceIds.length === 0) {
				continue;
			}

			try {
				const result = await this.dependencies.rollbackCreatedReferences(createdReferenceIds);
				rollbackDiagnostics.push(...result.diagnostics);
				createdReferenceIds.forEach((referenceId) => {
					const item = result.items.find((candidate) => candidate.referenceId === referenceId);
					if (item?.status === 'removed') {
						return;
					}
					if (item?.diagnostics.some((diagnostic) => diagnostic.level === 'error')) {
						return;
					}
					rollbackDiagnostics.push(createExecutionDiagnostic({
						kind: 'cascade-remove-failed',
						level: 'error',
						message: `创建回滚未能确认引用已经删除: ${referenceId}`,
						referenceId,
					}));
				});
			} catch (error) {
				rollbackDiagnostics.push(createExecutionDiagnostic({
					kind: 'cascade-remove-failed',
					level: 'error',
					message: `创建回滚执行失败: ${error instanceof Error ? error.message : String(error)}`,
					referenceId: createdReferenceIds[0],
				}));
			}
		}

		return rollbackDiagnostics;
	}
}
